import { Delete } from "@material-ui/icons";
import { Button, Card, Input, Row } from "antd";
import styled from "styled-components";

import BaseRangeSlider from "components/base/BaseRangeSlider";
import { ColorEditor } from "components/shared";

const NOT_APPLICABLE_FALLBACK = "#AAAAAA";
const OTHERS_FALLBACK = "#E0E0E0";

const PaletteColors = ({
  selectedPalette,
  thickness,
  numberOfColors,
  setSelectedPalette,
  setIsChanged,
  updateThickness,
  setNumberOfColors
}) => {
  return (
    <ColorsContainer>
      <ContentRow>
        <SpacedBetweenRow className="new-color-btn-row">
          <Row>
            <label>Number of Colours</label>
            <Input
              type="number"
              aria-label="number of colors"
              min={1}
              placeholder="Number of Colours"
              max={100}
              value={numberOfColors}
              step={1}
              onChange={(evt) => {
                setNumberOfColors(parseInt(evt.target.value));
              }}
            />
          </Row>
          <Row>
            <label>Line Thickness</label>
            <BaseRangeSlider
              value={thickness}
              max={10}
              step={0.5}
              unit={"pt"}
              defaultValue={2}
              onChange={(val) => {
                updateThickness(val);
              }}
            />
          </Row>
        </SpacedBetweenRow>
        <Card>
          {(selectedPalette?.colors ?? []).map((color, i) => {
            return (
              <ColorRow key={selectedPalette.name + ":" + i}>
                <div>Group {i + 1}</div>
                <ColorEditor
                  color={color}
                  thickness={selectedPalette.thickness[i]}
                  disableTransparent={true}
                  onChange={function (color: string, thickness: number): void {
                    const newColor = selectedPalette.colors;
                    newColor[i] = color;

                    const newThickness = selectedPalette.thickness ?? [];
                    newThickness[i] = thickness;

                    const updatedPallet = Object.assign({}, selectedPalette, {
                      colors: [...newColor],
                      thickness: [...newThickness]
                    });

                    setSelectedPalette(updatedPallet);
                    setIsChanged(true);
                  }}
                />
                <Button
                  icon={<Delete />}
                  type="ghost"
                  onClick={() => {
                    selectedPalette.colors.splice(i, 1);
                    selectedPalette.thickness.splice(i, 1);
                    setSelectedPalette(
                      Object.assign({}, selectedPalette, {
                        colors: [...selectedPalette.colors],
                        thickness: [...selectedPalette.thickness]
                      })
                    );
                    setNumberOfColors((prevNumberOfColors) => prevNumberOfColors - 1);
                  }}
                  className="color-delete-btn"
                />
              </ColorRow>
            );
          })}
          <Button
            className="color-add-btn"
            onClick={() => {
              setNumberOfColors((prevNumberOfColors) => prevNumberOfColors + 1);
            }}>
            + Add Colour
          </Button>
          <ColorRowDivider />
          <ColorRow>
            <div>n/a</div>
            <ColorEditor
              color={
                selectedPalette?.specialColors?.notApplicableColor ??
                NOT_APPLICABLE_FALLBACK
              }
              thickness={thickness ?? 2}
              disableTransparent={true}
              onChange={function (color: string): void {
                // The thickness slider re-emits the displayed colour; don't pin an unset default.
                if (
                  selectedPalette?.specialColors?.notApplicableColor == null &&
                  color === NOT_APPLICABLE_FALLBACK
                )
                  return;
                setSelectedPalette(
                  Object.assign({}, selectedPalette, {
                    specialColors: {
                      ...(selectedPalette?.specialColors ?? {}),
                      notApplicableColor: color
                    }
                  })
                );
              }}
            />
          </ColorRow>
          <ColorRow>
            <div>Others</div>
            <ColorEditor
              color={selectedPalette?.specialColors?.otherBinColor ?? OTHERS_FALLBACK}
              thickness={thickness ?? 2}
              disableTransparent={true}
              onChange={function (color: string): void {
                // The thickness slider re-emits the displayed colour; don't pin an unset default.
                if (
                  selectedPalette?.specialColors?.otherBinColor == null &&
                  color === OTHERS_FALLBACK
                )
                  return;
                setSelectedPalette(
                  Object.assign({}, selectedPalette, {
                    specialColors: {
                      ...(selectedPalette?.specialColors ?? {}),
                      otherBinColor: color
                    }
                  })
                );
              }}
            />
          </ColorRow>
        </Card>
      </ContentRow>
    </ColorsContainer>
  );
};

export default PaletteColors;

const ColorsContainer = styled.div`
  width: 600px;
  padding-right: 100px;
`;

const ContentRow = styled(Row)`
  padding: 5px;
  padding-top: 10px;
  display: flex;
  width: 100%;
  flex-direction: row;
  .ant-card {
    height: 400px;
    max-height: 400px;
    overflow-y: auto;
  }

  .interopolate-title {
    gap: 4px;
    display: flex;
    align-items: center;
  }

  .color-add-btn {
    padding: 5px;
    margin-bottom: 10px;
    color: var(--color-primary);
    border-style: none;
    outline: none;
    box-shadow: none;
    &:hover {
      color: var(--color-text);
    }
  }
`;

const ColorRowDivider = styled.div`
  width: 100%;
  border-bottom: 1px solid #e8e8e8;
`;
const ColorRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 5px;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  padding-right: 30px;
  gap: 10px;
  min-height: 35px;
  div {
    min-width: 200px;
  }
  &:hover {
    .color-delete-btn {
      display: flex !important;
    }
  }
  .color-delete-btn {
    display: none !important;
    min-width: 30px;
    align-items: center;
    justify-content: center;
    height: 24px;
    color: red;
  }
`;

const SpacedBetweenRow = styled(Row)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-right: 30px;
  .ant-input {
    max-width: 300px;
  }
`;
